import { QRCodeSVG } from 'qrcode.react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import wsLogo from '../../assets/images/wonderschool/logo-base.png';

// @handoff Where the "get the app" code points.
//
// This replaces a baked-in PNG (assets/images/app-download-qr.png) whose contents
// nobody could read from the codebase. It encoded a Bitly link-in-bio page, and that
// same URL is kept here so nothing changes for providers who reprint today. The
// `?r=qr` suffix is Bitly's scan-attribution parameter, so keep it if the URL stays.
//
// This is the one line to change once we have a Wonderschool-owned download URL.
// Generating the code from a constant (instead of shipping an image) is the whole
// point: the destination becomes visible, reviewable, and a one-line edit.
//
// Keep the replacement URL short. A longer string needs more QR modules, which shrinks
// each printed square. Measured at DOWNLOAD_CODE_PX below, this Bitly URL prints at
// 0.56mm per module and a long one like `www.wonderschool.com/get-the-app?src=poster`
// at 0.52mm. Scanners want roughly 0.5mm or more, so there is almost no headroom left:
// a much longer URL needs a bigger DOWNLOAD_CODE_PX to stay readable.
const APP_DOWNLOAD_URL = 'https://bit.ly/m/wonderschool?r=qr';

// Printed ink. Kept to two tones and no filled backgrounds so the poster prints
// legibly on any printer without burning through toner.
const INK = '#28325A';
const INK_MUTED = '#6B7288';
const RULE = '#D6DAE5';

// Physical scan sizes. Rough industry guidance is a 10:1 ratio of scan distance to
// code width: the check-in code is scanned by someone standing back from a door, so
// it stays large; the download code is scanned from arm's length, so it can be small.
// Both stay dark on white — differentiation between the two codes comes from size,
// position and labels, never from lightening one, which would cost scan reliability.
// Note these are the SVG's full width, which INCLUDES the 4-module quiet zone `includeMargin`
// adds on each side. So a 33-module code at 280px is laid out over 41 units, not 33, and one
// printed module is 280/41 = 1.81mm — not 280/33. Divide by modules + 8, or you will
// overestimate how big the printed squares actually are.
const CHECK_IN_CODE_PX = 280;
const DOWNLOAD_CODE_PX = 104;

// Page budget: the rendered poster has to clear a single US Letter sheet. Verified by
// printing it to PDF and confirming a 1-page, 612x792pt output. If you add a row or grow a
// code, re-check that — a poster that spills onto page 2 puts the store codes on a sheet
// nobody pins up.

// Error correction. qrcode.react defaults to 'L' (~7% recoverable), which is the wrong
// default for paper that gets taped to a doorframe and then scuffed, splashed and
// sun-faded. 'Q' recovers ~25%. For a 20-character Firestore organization id this is
// free: both L and Q encode to a version-4, 33-module code, so the check-in poster gets
// the sturdier code at no cost in density.
const ERROR_CORRECTION = 'Q';

// The card is portaled to <body> (a sibling of the #root app tree). On screen it
// is hidden. When printing, every OTHER direct child of <body> — the app, modals,
// toasts — is set to display:none, which collapses the document to just this one
// card so it prints as a single clean page (no blank leading page). Shared by the
// Check In Check Out onboarding page and School Settings so the printed poster
// looks the same everywhere.
//
// Note the two code-specific selectors: a single `.org-qr-print-area svg` rule would
// force BOTH codes to the same size, which is what the earlier single-code version did.
const PRINT_STYLE = `
@media print {
  @page { size: portrait; margin: 0.5in; }
  body > *:not(.org-qr-print-area) { display: none !important; }
  .org-qr-print-area {
    display: block !important;
    box-sizing: border-box;
    width: 100%;
    padding: 16px 24px;
    text-align: center;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .org-qr-print-area img { max-width: none !important; }
  .org-qr-checkin-code svg { width: ${CHECK_IN_CODE_PX}px !important; height: ${CHECK_IN_CODE_PX}px !important; }
  .org-qr-download-code svg { width: ${DOWNLOAD_CODE_PX}px !important; height: ${DOWNLOAD_CODE_PX}px !important; }
}
`;

interface OrgQrPrintCardProps {
  /** The check-in code's payload. Unchanged: the app scanner compares this string exactly. */
  value: string;
  /** Program name printed above the code, so a poster on a wall identifies itself. */
  organizationName?: string;
}

const OrgQrPrintCard = ({ value, organizationName }: OrgQrPrintCardProps) => {
  const { t } = useTranslation();

  if (!value || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <style>{PRINT_STYLE}</style>
      <div className="org-qr-print-area hidden" style={{ textAlign: 'center' }}>
        <img
          src={wsLogo}
          alt="Wonderschool"
          style={{ width: 150, height: 'auto', maxWidth: 'none', display: 'inline-block' }}
        />

        {organizationName && (
          <h1 style={{ margin: '18px 0 0', fontSize: 26, fontWeight: 600, color: INK }}>{organizationName}</h1>
        )}

        <p style={{ margin: '20px 0 10px', fontSize: 14, fontWeight: 600, letterSpacing: '0.08em', color: INK }}>
          {t('qrCode.scanToCheckIn')}
        </p>

        <div className="org-qr-checkin-code" style={{ display: 'block' }}>
          <QRCodeSVG
            value={value}
            size={CHECK_IN_CODE_PX}
            level={ERROR_CORRECTION}
            fgColor={INK}
            bgColor="#FFFFFF"
            includeMargin
          />
        </div>

        <h2 style={{ margin: '18px auto 0', maxWidth: 460, fontSize: 20, fontWeight: 600, color: INK }}>
          {t('qrCode.title')}
        </h2>

        <ol
          style={{
            display: 'inline-block',
            margin: '12px 0 0',
            paddingLeft: 24,
            maxWidth: 420,
            fontSize: 15,
            lineHeight: 1.7,
            color: INK,
            listStyleType: 'decimal',
            textAlign: 'left',
          }}
        >
          <li>{t('qrCode.step1')}</li>
          <li>{t('qrCode.step2')}</li>
          <li>{t('qrCode.step3')}</li>
          <li>{t('qrCode.step4')}</li>
          <li>{t('qrCode.step5')}</li>
        </ol>

        {/* Get-the-app zone. Below a rule, at roughly a third the size, and labelled,
            so it cannot be mistaken for the check-in code above it. */}
        <div style={{ margin: '56px auto 0', maxWidth: 460, borderTop: `1px solid ${RULE}`, paddingTop: 20 }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: INK }}>{t('qrCode.noAppYet')}</p>

          <div className="org-qr-download-code" style={{ display: 'block', marginTop: 12 }}>
            <QRCodeSVG
              value={APP_DOWNLOAD_URL}
              size={DOWNLOAD_CODE_PX}
              level={ERROR_CORRECTION}
              fgColor={INK}
              bgColor="#FFFFFF"
              includeMargin
            />
          </div>

          <p style={{ margin: '8px 0 0', fontSize: 13, color: INK_MUTED }}>{t('qrCode.scanToDownloadFree')}</p>
        </div>
      </div>
    </>,
    document.body
  );
};

export default OrgQrPrintCard;
